/**
 * TypeScript Interfaces for Photostudio Feature
 *
 * AI-powered image generation studio with cinematographic controls
 * for social media visuals. Integrates with AI Avatar reference persons.
 */

import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import type { Id } from '../../convex/_generated/dataModel';

// ============================================================================
// Constants
// ============================================================================

const PHOTOSTUDIO_CONSTANTS = {
  MAX_NAME_LENGTH: 100,
  MAX_DESCRIPTION_LENGTH: 500,
  MAX_IMAGES_PER_GENERATION: 4,
  MAX_IMAGE_SIZE_MB: 10,
  ACCEPTED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/webp'] as const,
} as const;

// ============================================================================
// Enums / Literal Types
// ============================================================================

export const ASPECT_RATIOS = ['1:1', '4:5', '9:16', '16:9'] as const;
export type AspectRatio = (typeof ASPECT_RATIOS)[number];

export const SHOT_TYPES = [
  "Bird's-Eye View",
  'Close Up',
  'Cowboy Shot',
  'Cutaway Shot',
  'Dutch Angle',
  'Entire Body',
  'Establishing Shot',
  'Extreme Close Up',
  'Group Shot',
  'Headshot',
  'High Angle Shot',
  'Insert Shot',
  'Low Angle Shot',
  'Medium Shot',
  'Over The Shoulder',
  'Overhead Shot',
  'Point of View (POV)',
  'Two Shot',
  'Wide Shot',
  'Medium Close Up',
  'Full Shot',
  'American Shot',
  'Master Shot',
  'Reaction Shot',
  'Tracking Shot',
] as const;
export type ShotType = (typeof SHOT_TYPES)[number];

export const LIGHTING_TYPES = [
  'Backlighting',
  'Bounce Lighting',
  'Broad Lighting',
  'Candlelight',
  'Chiaroscuro',
  'Color Gels',
  'Gobo Lighting',
  'Hard Lighting',
  'High Key',
  'Loop Lighting',
  'Low Key',
  'Natural Light',
  'Paramount (Butterfly)',
  'Practical Light',
  'Rembrandt',
  'Rim Lighting',
  'Short Lighting',
  'Side Lighting',
  'Silhouette',
  'Soft Lighting',
  'Split Lighting',
  'Studio Lighting',
  'Golden Hour',
  'Blue Hour',
] as const;
export type LightingType = (typeof LIGHTING_TYPES)[number];

export const LENS_TYPES = [
  'Standard',
  'Wide Angle',
  'Telephoto',
  'Macro',
  'Portrait (85mm)',
  'Anamorphic',
  'Shallow DOF',
  'Deep DOF',
] as const;
export type LensType = (typeof LENS_TYPES)[number];

export const CAMERA_BODIES = [
  'Digital Cinema',
  'Super 16mm Film',
  'Vintage Film',
  'DSLR',
  'Medium Format',
  'Lomography',
  'Polaroid',
] as const;
export type CameraBody = (typeof CAMERA_BODIES)[number];

export const FILM_STOCKS = [
  'None',
  'Kodak Portra',
  'Fuji Superia',
  'Cinestill 800T',
  'Kodak Ektar',
  'Black & White',
] as const;
export type FilmStock = (typeof FILM_STOCKS)[number];

export const EFFECTS = [
  'Grain',
  'Light Leaks',
  'Underexposed',
  'Overexposed',
  'Vignette',
  'Lens Flare',
  'Motion Blur',
] as const;
export type Effect = (typeof EFFECTS)[number];

export const ELEMENT_TYPES = ['outfit', 'prop', 'scene'] as const;
export type ElementType = (typeof ELEMENT_TYPES)[number];

// ============================================================================
// Option Picker Types (Visual Selection with Thumbnails)
// ============================================================================

export type OptionPickerCategory =
  | 'shot-types'
  | 'lighting'
  | 'lens'
  | 'camera-body'
  | 'film-stock'
  | 'effects'
  | 'focal-length'
  | 'photography-genre';

export interface IPhotostudioOption {
  value: string;
  label: string;
  description?: string;
}

export interface IOptionPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  options: IPhotostudioOption[];
  selectedValue?: string;
  onSelect: (value: string) => void;
  category: OptionPickerCategory;
}

export interface IOptionCardProps {
  option: IPhotostudioOption;
  isSelected: boolean;
  isHovered: boolean;
  onSelect: () => void;
  onHover: (element: HTMLElement) => void;
  onHoverEnd: () => void;
  category: OptionPickerCategory;
}

export interface IVisualSelectTriggerProps {
  value?: string;
  placeholder: string;
  onClick: () => void;
  disabled?: boolean;
  category: OptionPickerCategory;
}

/**
 * Get the thumbnail path for a photostudio option
 */
export function getOptionThumbnailPath(category: OptionPickerCategory, value: string): string {
  const slug = value.toLowerCase().replace(/[()]/g, '').replace(/\s+/g, '-').replace(/'/g, '');
  return `/images/photostudio-previews/${category}/${slug}.png`;
}

/**
 * Get placeholder thumbnail path when actual thumbnail is not available
 */
export function getPlaceholderThumbnailPath(_category: OptionPickerCategory): string {
  return `/images/photostudio-previews/placeholder.svg`;
}

export const GENERATION_MODES = ['variations', 'angles'] as const;
export type GenerationMode = (typeof GENERATION_MODES)[number];

export const FOCAL_LENGTHS = [
  '14mm',
  '24mm',
  '35mm',
  '50mm',
  '85mm',
  '135mm',
  '200mm',
  '400mm',
] as const;

export const PHOTOGRAPHY_GENRES = [
  'Portrait',
  'Fashion',
  'Street',
  'Documentary',
  'Editorial',
  'Commercial',
  'Fine Art',
  'Lifestyle',
  'Product',
  'Architecture',
  'Landscape',
  'Sports',
  'Wildlife',
] as const;

export const RESOLUTIONS = ['512px', '1K', '2K', '4K'] as const;

// ============================================================================
// Core Data Types
// ============================================================================

export interface IPhotostudioSettings {
  style?: string;
  shotType?: ShotType;
  lighting?: LightingType;
  lens?: LensType;
  aspectRatio: AspectRatio;
  cameraBody?: CameraBody;
  filmStock?: FilmStock;
  effects?: Effect[];
  lightingDetails?: string;
}

export interface IPhotostudioReferences {
  characterPersonId?: Id<'referencePersons'>;
  characterUploadId?: Id<'_storage'>;
  outfitId?: Id<'photostudioElements'>;
  outfitUploadId?: Id<'_storage'>;
  propId?: Id<'photostudioElements'>;
  propUploadId?: Id<'_storage'>;
  sceneId?: Id<'photostudioElements'>;
  sceneUploadId?: Id<'_storage'>;
}

export interface IPhotostudioProject {
  _id: Id<'photostudioProjects'>;
  _creationTime: number;
  userId: Id<'users'>;
  name: string;
  description?: string;
  thumbnailId?: Id<'_storage'>;
  thumbnailUrl?: string;
  imageCount: number;
  isDeleted: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface IPhotostudioElement {
  _id: Id<'photostudioElements'>;
  _creationTime: number;
  userId: Id<'users'>;
  type: ElementType;
  name: string;
  description?: string;
  imageId: Id<'_storage'>;
  imageUrl?: string;
  isDeleted: boolean;
  createdAt: number;
}

// ============================================================================
// Session State Types (not persisted)
// ============================================================================

export interface ISessionUpload {
  file: File;
  preview: string;
  storageId?: Id<'_storage'>;
}

export interface IElementSlotState {
  type: 'empty' | 'reference-person' | 'library' | 'session-upload';
  referencePersonId?: Id<'referencePersons'>;
  elementId?: Id<'photostudioElements'>;
  sessionUpload?: ISessionUpload;
  saveToLibrary?: boolean;
  /** Auto-generated or user-provided description for session uploads */
  description?: string;
}

/**
 * Default character selection for Photostudio auto-population
 * Priority: Reference person images → Default AI avatar fallback
 */
export interface IDefaultCharacter {
  type: 'reference-person';
  referencePersonId: Id<'referencePersons'>;
  name: string;
  imageUrl: string;
  source: 'reference-person' | 'ai-avatar';
}

export interface IGeneratedImage {
  storageId: Id<'_storage'>;
  url: string;
  selected: boolean;
}

// ============================================================================
// Reusable Form Component Props
// ============================================================================

export interface ILabeledSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly string[] | string[];
  placeholder?: string;
  disabled?: boolean;
}

export interface ILabeledTextareaProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
}

export interface IFormSectionProps {
  number?: string;
  title: string;
  icon: LucideIcon;
  defaultOpen?: boolean;
  children: ReactNode;
  /** Section color key: '1' | '2' | '3' | 'references' - maps to CSS variables */
  sectionColor?: '1' | '2' | '3' | 'references';
}

// ============================================================================
// Component Props
// ============================================================================

export interface ISaveToProjectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (projectId: Id<'photostudioProjects'> | 'new', newProjectName?: string) => void;
  selectedCount: number;
  isSaving: boolean;
}

export interface IReferenceCardProps {
  label: string;
  icon: LucideIcon;
  imageUrl?: string | null;
  onClick: () => void;
  onClear?: () => void;
  disabled?: boolean;
}

export interface IConstructedPromptProps {
  prompt: string;
  onEdit?: () => void;
}

export interface IRenderAreaProps {
  imageUrl?: string | null;
  isLoading?: boolean;
  label?: string;
  onDownload?: () => void;
  onSave?: () => void;
}

export interface IActionButtonProps {
  variant: 'primary' | 'secondary';
  icon?: LucideIcon;
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  title?: string;
}

export interface IElementPickerDialogProps {
  isOpen: boolean;
  elementType: ElementType;
  onClose: () => void;
  onSelect: (elementId: Id<'photostudioElements'>) => void;
}

// ============================================================================
// PhotostudioPageNew Sub-Component Props
// ============================================================================

export type VisualPickerDialog =
  | 'shotType'
  | 'lighting'
  | 'cameraBody'
  | 'lensType'
  | 'filmStock'
  | 'focalLength'
  | 'photographyGenre'
  | null;

export interface IVisualPickerConfig {
  title: string;
  options: IPhotostudioOption[];
  selectedValue: string;
  onSelect: (value: string) => void;
  category: OptionPickerCategory;
}

export interface IPhotostudioFormColumnProps {
  subjectAction: string;
  setSubjectAction: (v: string) => void;
  environment: string;
  setEnvironment: (v: string) => void;
  shotType: string;
  lightingSource: string;
  photographyGenre: string;
  atmosphere: string;
  setAtmosphere: (v: string) => void;
  cameraBody: string;
  focalLength: string;
  lensType: string;
  filmStock: string;
  isGenerating: boolean;
  personPreviewUrl: string | undefined;
  outfitPreviewUrl: string | undefined;
  scenePreviewUrl: string | undefined;
  characterSlot: IElementSlotState;
  outfitSlot: IElementSlotState;
  sceneSlot: IElementSlotState;
  onOpenPersonPicker: () => void;
  onOpenElementPicker: (type: ElementType) => void;
  onClearPerson: () => void;
  onClearOutfit: () => void;
  onClearScene: () => void;
  setVisualPickerOpen: (v: VisualPickerDialog) => void;
}

export interface IPhotostudioPreviewColumnProps {
  constructedPrompt: string;
  resolution: string;
  setResolution: (v: string) => void;
  aspectRatio: string;
  setAspectRatio: (v: string) => void;
  isReadyToGenerate: boolean;
  isGenerating: boolean;
  generatedImageUrl: string | null;
  error: string | null;
  onGenerate: () => void;
  onDownload: () => void;
  onOpenSaveDialog: () => void;
}

// ============================================================================
// Hook Return Types
// ============================================================================

export interface IUsePhotostudioGenerationReturn {
  images: IGeneratedImage[];
  isGenerating: boolean;
  error: string | null;
  generate: (params: {
    subject: string;
    environment: string;
    settings: IPhotostudioSettings;
    references: IPhotostudioReferences;
    count: 1 | 2 | 3 | 4;
    mode: GenerationMode;
    customPrompt?: string;
    stylePrompt?: string;
  }) => Promise<void>;
  toggleSelect: (index: number) => void;
  selectAll: () => void;
  deselectAll: () => void;
  clearImages: () => void;
  selectedCount: number;
  getSelectedImages: () => Array<{ storageId: Id<'_storage'>; url: string }>;
}

export interface IUsePhotostudioProjectsReturn {
  projects: IPhotostudioProject[];
  isLoading: boolean;
  error: string | null;
  createProject: (
    name: string,
    description?: string
  ) => Promise<{ success: boolean; projectId?: Id<'photostudioProjects'>; error?: string }>;
  updateProject: (
    projectId: Id<'photostudioProjects'>,
    name: string,
    description?: string
  ) => Promise<{ success: boolean; error?: string }>;
  deleteProject: (
    projectId: Id<'photostudioProjects'>
  ) => Promise<{ success: boolean; error?: string }>;
  saveImagesToProject: (
    projectId: Id<'photostudioProjects'>,
    images: Array<{
      storageId: Id<'_storage'>;
      prompt: string;
      settings: IPhotostudioSettings;
      references: IPhotostudioReferences;
    }>
  ) => Promise<{ success: boolean; error?: string }>;
}

export interface IUsePhotostudioElementsReturn {
  elements: IPhotostudioElement[];
  outfits: IPhotostudioElement[];
  props: IPhotostudioElement[];
  scenes: IPhotostudioElement[];
  isLoading: boolean;
  error: string | null;
  createElement: (
    type: ElementType,
    name: string,
    imageId: Id<'_storage'>,
    description?: string
  ) => Promise<{ success: boolean; elementId?: Id<'photostudioElements'>; error?: string }>;
  updateElement: (
    elementId: Id<'photostudioElements'>,
    name: string,
    description?: string
  ) => Promise<{ success: boolean; error?: string }>;
  deleteElement: (
    elementId: Id<'photostudioElements'>
  ) => Promise<{ success: boolean; error?: string }>;
  uploadAndCreateElement: (
    type: ElementType,
    name: string,
    file: File,
    description?: string
  ) => Promise<{ success: boolean; elementId?: Id<'photostudioElements'>; error?: string }>;
  getElementsByType: (type: ElementType) => IPhotostudioElement[];
}

// ============================================================================
// Element Edit Dialog Reducer Types
// ============================================================================

export interface IEditDialogState {
  name: string;
  description: string;
  isSubmitting: boolean;
  formError: string | null;
}

export type IEditDialogAction =
  | { type: 'RESET'; payload: { name: string; description: string } }
  | { type: 'SET_NAME'; payload: string }
  | { type: 'SET_DESCRIPTION'; payload: string }
  | { type: 'SET_SUBMITTING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR' };

// ============================================================================
// Element Upload Dialog Reducer Types
// ============================================================================

export interface IUploadFormState {
  name: string;
  description: string;
  selectedFile: File | null;
  previewUrl: string | null;
  isSubmitting: boolean;
  formError: string | null;
}

export type IUploadFormAction =
  | { type: 'SET_NAME'; payload: string }
  | { type: 'SET_DESCRIPTION'; payload: string }
  | { type: 'SET_FILE'; payload: { file: File; previewUrl: string } }
  | { type: 'CLEAR_FILE' }
  | { type: 'SET_SUBMITTING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'RESET' };

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Validate image file for upload
 * Checks file type and size against constants
 */
export function validateImageFile(file: File): { valid: boolean; error?: string } {
  const { MAX_IMAGE_SIZE_MB, ACCEPTED_IMAGE_TYPES } = PHOTOSTUDIO_CONSTANTS;

  if (!ACCEPTED_IMAGE_TYPES.includes(file.type as (typeof ACCEPTED_IMAGE_TYPES)[number])) {
    return { valid: false, error: 'Only JPEG, PNG, and WebP images are accepted' };
  }

  if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
    return { valid: false, error: `Image must be less than ${MAX_IMAGE_SIZE_MB}MB` };
  }

  return { valid: true };
}

/**
 * Build empty element slot state
 */
export function getEmptySlotState(): IElementSlotState {
  return { type: 'empty' };
}
